import { Box, Flex } from '@televet/kibble-ui/chakra';
import React, { useRef } from 'react';
import { DownloadIcon } from './DownloadIcon';

function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function triggerCsvDownload(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportableTable({
  children,
  ...props
}: {
  children: React.ReactNode;
  [key: string]: any;
}) {
  const tableRef = useRef<HTMLTableElement>(null);

  const handleExport = () => {
    if (!tableRef.current) return;

    const headerCells = tableRef.current.querySelectorAll('thead th');
    const bodyRows = tableRef.current.querySelectorAll('tbody tr');

    const csvRows: string[] = [];

    if (headerCells.length > 0) {
      csvRows.push(
        Array.from(headerCells)
          .map((th) => escapeCsvCell(th.textContent?.trim() ?? ''))
          .join(',')
      );
    }

    bodyRows.forEach((tr) => {
      const cells = tr.querySelectorAll('td');
      csvRows.push(
        Array.from(cells)
          .map((td) => escapeCsvCell(td.textContent?.trim() ?? ''))
          .join(',')
      );
    });

    triggerCsvDownload(csvRows.join('\n'), `otto-export-${Date.now()}.csv`);
  };

  return (
    <Box position="relative">
      <div style={{ overflowX: 'auto', margin: '1em 0' }}>
        <table
          ref={tableRef}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e2e8f0',
            borderRadius: '4px',
          }}
          {...props}
        >
          {children}
        </table>
      </div>
      <Flex
        as="button"
        position="absolute"
        top="0"
        right="0"
        align="center"
        gap="4px"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        px="6px"
        py="2px"
        fontSize="xs"
        color="gray.600"
        cursor="pointer"
        onClick={handleExport}
        title="Export table to CSV"
        _hover={{ bg: 'gray.50', color: 'gray.800' }}
      >
        <DownloadIcon />
        CSV
      </Flex>
    </Box>
  );
}
